import React, { useEffect, useMemo } from 'react';
import { Select, SelectItem, Selection } from '@nextui-org/react';
import { MarketData, SettlementDetails } from 'common/src/utils/types';
import { useTranslation } from '../../hooks/useTranslation';

interface LabelFilterProps {
  data: MarketData[] | SettlementDetails[];
  selectedLabel: string | string[];
  onSelectionChange: (value: string[]) => void;
  selectionMode?: 'single' | 'multiple';
  label?: string;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  defaultSelectedKeys?: string[];
  defaultSelectAll?: boolean;
  hideAllOption?: boolean;
}

const LabelFilter: React.FC<LabelFilterProps> = ({
  data,
  selectedLabel,
  onSelectionChange,
  selectionMode = 'single',
  label,
  className = 'min-w-32',
  size = 'sm',
  defaultSelectedKeys,
  defaultSelectAll = false,
  hideAllOption = false,
}) => {
  const { t } = useTranslation('common');
  const finalLabel = label || t('Etiqueta');
  const labels = useMemo(() => {
    // Create a map to count records per label
    const labelCount = data.reduce((acc, item) => {
      const label = item.label || 'Sin Etiqueta';
      if (label) {
        acc[label] = (acc[label] || 0) + 1;
      }
      return acc;
    }, {} as Record<string, number>);

    // Convert to array and sort by count
    return Object.entries(labelCount)
      .sort(([, countA], [, countB]) => countB - countA)
      .map(([label]) => label);
  }, [data]);

  useEffect(() => {
    if (selectionMode === 'multiple' && defaultSelectAll && labels.length > 0) {
      onSelectionChange(labels);
    }
  }, []);

  if (labels.length === 0) {
    return null;
  }

  const handleSelectionChange = (keys: Selection) => {
    if (selectionMode === 'multiple') {
      const keysArray = Array.from(keys);

      if (keysArray.includes('all')) {
        // If 'all' is selected and there were previous selections, clear all
        if (Array.isArray(selectedLabel) && selectedLabel.length > 0) {
          onSelectionChange([]);
        } else {
          // Otherwise, select all labels
          onSelectionChange(labels);
        }
      } else {
        // Normal selection behavior for individual items
        onSelectionChange(keysArray as string[]);
      }
    } else {
      // Keep existing behavior for single selection
      const selectedKeys = Array.from(keys);
      onSelectionChange(selectedKeys as string[]);
    }
  };

  return (
    <Select
      label={finalLabel}
      selectedKeys={
        selectionMode === 'multiple'
          ? Array.isArray(selectedLabel)
            ? new Set(selectedLabel)
            : new Set([])
          : new Set([selectedLabel as string])
      }
      onSelectionChange={handleSelectionChange}
      selectionMode={selectionMode}
      defaultSelectedKeys={defaultSelectedKeys && new Set(defaultSelectedKeys)}
      className={className}
      size={size}
      aria-label={`${t('Selecciona etiqueta')}`}
      renderValue={(items) => {
        if (
          selectionMode === 'multiple' &&
          Array.isArray(selectedLabel) &&
          selectedLabel.length === labels.length
        ) {
          return (
            <div className='flex items-center gap-2'>{t('Todas las etiquetas')}</div>
          );
        }
        if (Array.isArray(selectedLabel) && selectedLabel.length > 0) {
          return (
            <div className='flex items-center gap-2'>
              {selectedLabel.join(', ')}
            </div>
          );
        }
        return items;
      }}
    >
      {selectionMode === 'single' && !hideAllOption && (
        <SelectItem key='all' value='all' textValue={t('Todos')}>
          {t('Todos')}
        </SelectItem>
      )}
      {selectionMode === 'multiple' && !hideAllOption && (
        <SelectItem key='all' value='all' textValue={t('Todos')}>
          <div className='flex justify-between items-center'>
            <span>
              {Array.isArray(selectedLabel) && selectedLabel.length > 0
                ? t('Deseleccionar todos')
                : t('Seleccionar todos')}
            </span>
          </div>
        </SelectItem>
      )}
      {labels.map((label) => (
        <SelectItem key={label} value={label} textValue={label}>
          {label}
        </SelectItem>
      ))}
    </Select>
  );
};

export default React.memo(LabelFilter);
